import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import history from 'browserHistory'

import RoutesHelper from 'helpers/RoutesHelper'

import { getActiveNote } from 'data/notes/selectors'
import { getActiveChannel, getPersonalChannel } from 'data/channels/selectors'
import { getActiveFilterId } from 'data/ui/selectors'
import { hasSeenNearNoteLimitIncentiveAt } from 'data/userPreferences/selectors'
import {
  isNotesUsageLimitReached,
  isNotesUsageLimitNear,
} from 'data/organizationResourcesUsage/selectors'
import { updateUserPreferences } from 'data/userPreferences/actions'
import { createErrorToasterMessage } from 'data/ui/actions'
import { trackNewNoteClicked } from 'data/analytics/actions'

import IncentiveModal from 'screens/Sidebar/NewSidebar/ContextualNewNoteButton/IncentiveModal'
import IncentiveBox from 'components/Utils/IncentiveBox'

import { provideCreateNote } from '../dataSync/provideCreateNote'
import NewNoteLimitIncentive from './NewNoteLimitIncentive'
import { NewNoteButtonLayout } from './NewNoteButtonLayout'

export type ContextualNewNoteButtonProps = {
  className?: string
  createNote: (
    options: {
      channelId?: string
      parentNoteId?: string
    }
  ) => Promise<{ result: { data: { createNote: any } } }>
}

const ContextualNewNoteButton = ({
  className,
  createNote,
}: ContextualNewNoteButtonProps) => {
  const dispatch = useDispatch()
  const activeNote = useSelector(getActiveNote)
  const activeChannel = useSelector(getActiveChannel)
  const activeFilterId = useSelector(getActiveFilterId)
  const personalChannel = useSelector(getPersonalChannel)
  const hasSeenNearNoteLimitIncentive = !!useSelector(
    hasSeenNearNoteLimitIncentiveAt
  )
  const limitReached = useSelector(isNotesUsageLimitReached)
  const nearLimit = useSelector(isNotesUsageLimitNear)

  const [incentiveModalShown, setIncentiveModalShown] = React.useState(false)

  const handleNoteCreation = async () => {
    if (nearLimit && !hasSeenNearNoteLimitIncentive) {
      dispatch(
        updateUserPreferences({
          seenNearNoteLimitIncentiveAt: Date.now(),
        })
      )
      setIncentiveModalShown(true)
      return
    }
    try {
      if (activeNote) {
        await createNoteAndRedirect(createNote, activeFilterId, {
          channelId: activeNote.channelId,
          parentNoteId: activeNote.id,
        })
        return
      }
      if (activeChannel) {
        await createNoteAndRedirect(createNote, activeFilterId, {
          channelId: activeChannel.id,
        })
        return
      }
      await createNoteAndRedirect(createNote, activeFilterId, {
        channelId: personalChannel.id,
      })
    } catch ({ error }) {
      if (!error || !error.errors || error.errors.length === 0) {
        return
      }
      dispatch(
        createErrorToasterMessage({
          title: 'Unable to create doc',
          detail:
            error.errors[0].errorType === 'UserError' &&
            error.errors[0].message,
        })
      )
    }
  }

  const handleIncentiveModalClose = () => {
    setIncentiveModalShown(false)
    handleNoteCreation()
  }

  const handleClick = () => {
    dispatch(trackNewNoteClicked({ source: 'new_note_button', depth: 0 }))
    handleNoteCreation()
  }

  return (
    <>
      {incentiveModalShown && (
        <IncentiveModal onModalClose={handleIncentiveModalClose} />
      )}
      <NewNoteButtonLayout
        className={className}
        onClick={handleClick}
        label={getLabel(activeNote, activeChannel)}
      />
      {nearLimit && !limitReached && <NewNoteLimitIncentive />}
      {limitReached && (
        <IncentiveBox
          className="entity-highlight margin-bottom-medium"
          incentiveId="NotesLimit"
          title="You have no free docs left"
          description="Upgrade to keep writing"
        />
      )}
    </>
  )
}

export default provideCreateNote(ContextualNewNoteButton)

const createNoteAndRedirect = async (
  createNote: ContextualNewNoteButtonProps['createNote'],
  activeFilterId: string,
  { channelId, parentNoteId }: { channelId?: string; parentNoteId?: string }
) => {
  const { result } = await createNote({
    channelId,
    parentNoteId,
  })
  const createdNote = result.data.createNote

  const nextUrl = activeFilterId
    ? RoutesHelper.noteFilterShow({
        filterId: activeFilterId,
        noteId: createdNote.id,
      })
    : RoutesHelper.noteChannelShow({ channelId, noteId: createdNote.id })

  history.push(nextUrl)
}

const getLabel = (activeNote: any, activeChannel: any) => {
  if (activeNote) {
    return (
      <>
        <div className="flexbox-no-shrink">New doc</div>
        <div className="font-weight-medium font-size-small transition ellipsis opacity-null opaque-child-effect">
          &nbsp;in {activeNote.title || ' Untitled doc'}
        </div>
      </>
    )
  }
  if (activeChannel) {
    return (
      <>
        <div className="flexbox-no-shrink">New doc</div>
        <div className="font-weight-medium font-size-small transition ellipsis opacity-null opaque-child-effect">
          &nbsp;in {activeChannel.name}
        </div>
      </>
    )
  }
  return 'New private doc'
}
